import { getItem, setItem } from '@alltrails/core/utils/localStorage';
import type { AdvancedBuilderState, PersistedAdvancedBuilder } from '@/utils/slices/advancedBuilderSlice';
import { isToggledMapContentEntry, mapContentEntriesById, type MapContentId } from './mapContent/mapContentRegistry';
import type { MapContentVisibility } from './mapContent/mapContentVisibility';
import { isPoiGroupId } from './mapContent/poiGroups';

// Holds the whole advancedBuilder slice; the name predates that and stays so existing users keep their settings
export const advancedBuilderStorageKey = 'advanced-mapping-content';

// Bump when the stored shape changes; a mismatch discards the stored value
const storageVersion = 1;

type StoredAdvancedBuilder = { v: number } & Partial<PersistedAdvancedBuilder>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// The reducer ignores style-backed ids, so a stored one could never be cleared
const isStorableContentId = (id: string) =>
  Object.hasOwn(mapContentEntriesById, id) && !isToggledMapContentEntry(mapContentEntriesById[id as MapContentId]);

const parseStoredVisibility = (contentVisibility: unknown): MapContentVisibility | undefined => {
  if (!isRecord(contentVisibility)) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(contentVisibility).filter(([id, isVisible]) => isStorableContentId(id) && typeof isVisible === 'boolean'));
};

const parseStoredExcludedPoiGroups = (excludedPoiGroups: unknown) =>
  Array.isArray(excludedPoiGroups) ? excludedPoiGroups.filter(isPoiGroupId) : undefined;

export const readStoredAdvancedBuilder = (): Partial<PersistedAdvancedBuilder> | undefined => {
  const stored = getItem<StoredAdvancedBuilder>(advancedBuilderStorageKey);
  if (!stored || stored.v !== storageVersion) {
    return undefined;
  }

  const contentVisibility = parseStoredVisibility(stored.contentVisibility);
  const excludedPoiGroups = parseStoredExcludedPoiGroups(stored.excludedPoiGroups);
  const restored = {
    ...(contentVisibility ? { contentVisibility } : {}),
    ...(excludedPoiGroups ? { excludedPoiGroups } : {}),
    // Only off is ever stored, so anything else is left to the default
    ...(stored.showGradeShading === false ? { showGradeShading: false } : {}),
    ...(stored.connectNewSegments === false ? { connectNewSegments: false } : {})
  };

  return Object.keys(restored).length > 0 ? restored : undefined;
};

// Named rather than spread, so a field added to the slice stays out of storage until someone decides it belongs there
export const writeStoredAdvancedBuilder = ({ connectNewSegments, contentVisibility, excludedPoiGroups, showGradeShading }: AdvancedBuilderState) =>
  setItem<StoredAdvancedBuilder>(advancedBuilderStorageKey, {
    connectNewSegments,
    contentVisibility,
    excludedPoiGroups,
    showGradeShading,
    v: storageVersion
  });
